export type RequestKind = 'new' | 'edit' | 'open-request' | 'blocked';
export type SubmitStatus = 'pending' | 'submitting' | 'done' | 'error';

export type ParsedItem = {
    file: File;
    relativePath: string;
    category: string;
    contentName: string;
};

export type PreviewItem = ParsedItem & {
    kind: RequestKind;
    contentId?: string;
    requestId?: string;
    newCategory?: boolean;
    note?: string;
    included: boolean;
    status: SubmitStatus;
    error?: string;
};

export type PreviewContent = {
    id?: string | null;
    name?: string | null;
    category?: string | null;
    applicationName?: string | null;
    currentRequestId?: string | null;
};

export type PreviewRequest = {
    id?: string | null;
    name?: string | null;
    category?: string | null;
    applicationName?: string | null;
};

const normalize = (value?: string | null) => (value ?? '').trim().toLocaleLowerCase();

export const getFileStem = (fileName: string) => {
    const lastDot = fileName.lastIndexOf('.');
    return lastDot > 0 ? fileName.substring(0, lastDot).trim() : fileName.trim();
};

export function parseFolderFile(file: File): ParsedItem | null {
    const relativePath = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
    if (!relativePath) return null;

    const parts = relativePath.split('/').slice(1);
    if (parts.length !== 2 || parts.some(part => !part.trim())) return null;

    const fileName = parts[1];
    if (fileName.startsWith('.') || fileName === 'Thumbs.db' || fileName === 'desktop.ini') return null;
    const lastDot = fileName.lastIndexOf('.');
    if (lastDot <= 0 || lastDot === fileName.length - 1) return null;

    return {
        file,
        relativePath,
        category: parts[0].trim(),
        contentName: fileName.substring(0, lastDot).trim(),
    };
}

export function buildPreview(
    parsed: ParsedItem[],
    contents: PreviewContent[],
    openNewRequests: PreviewRequest[],
    applicationName: string
): PreviewItem[] {
    const tupleCounts = new Map<string, number>();
    for (const item of parsed) {
        const key = `${normalize(item.contentName)}|${normalize(item.category)}|${normalize(applicationName)}`;
        tupleCounts.set(key, (tupleCounts.get(key) ?? 0) + 1);
    }

    const preview: PreviewItem[] = parsed.map(item => {
        const tupleKey = `${normalize(item.contentName)}|${normalize(item.category)}|${normalize(applicationName)}`;
        if ((tupleCounts.get(tupleKey) ?? 0) > 1) {
            return {
                ...item,
                kind: 'blocked',
                note: 'Another selected file resolves to the same name, category, and application.',
                included: false,
                status: 'pending',
            };
        }

        const sameNameAndCategory = contents.filter(content =>
            normalize(content.name) === normalize(item.contentName)
            && normalize(content.category) === normalize(item.category));
        const exactMatches = sameNameAndCategory.filter(content =>
            normalize(content.applicationName) === normalize(applicationName));

        if (exactMatches.length > 1) {
            return {
                ...item,
                kind: 'blocked',
                note: `${exactMatches.length} exact matches were found in this library.`,
                included: false,
                status: 'pending',
            };
        }

        if (exactMatches.length === 1) {
            const match = exactMatches[0];
            if (match.currentRequestId) {
                return {
                    ...item,
                    kind: 'blocked',
                    contentId: match.id ?? undefined,
                    note: 'The matched content already has an open request.',
                    included: false,
                    status: 'pending',
                };
            }
            return {
                ...item,
                kind: 'edit',
                contentId: match.id ?? undefined,
                note: 'Exact name, category, and application match.',
                included: true,
                status: 'pending',
            };
        }

        const matchingOpenRequests = openNewRequests.filter(request =>
            normalize(request.name) === normalize(item.contentName)
            && normalize(request.category) === normalize(item.category)
            && normalize(request.applicationName) === normalize(applicationName));
        if (matchingOpenRequests.length > 1) {
            return {
                ...item,
                kind: 'blocked',
                note: `${matchingOpenRequests.length} matching open new-content requests were found.`,
                included: false,
                status: 'pending',
            };
        }
        if (matchingOpenRequests.length === 1) {
            return {
                ...item,
                kind: 'open-request',
                requestId: matchingOpenRequests[0].id ?? undefined,
                note: 'A matching new-content request is already open. Ignored unless Submit draft is selected.',
                included: false,
                status: 'pending',
            };
        }

        const otherApplications = Array.from(new Set(
            sameNameAndCategory
                .map(content => content.applicationName?.trim())
                .filter((value): value is string => !!value)
        ));
        return {
            ...item,
            kind: 'new',
            note: otherApplications.length > 0
                ? `New request. Similar content exists for: ${otherApplications.join(', ')}.`
                : 'No exact match in the selected library.',
            included: true,
            status: 'pending',
        };
    });

    const existingCategories = new Set(
        [...contents, ...openNewRequests]
            .map(item => normalize(item.category))
            .filter(Boolean)
    );
    return preview.map(item => {
        const newCategory = !existingCategories.has(normalize(item.category));
        return {
            ...item,
            newCategory,
            note: newCategory
                ? `${item.note ?? ''} Category "${item.category}" is not currently used in this library.`.trim()
                : item.note,
        };
    });
}